import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import VideoCard from "@/components/feature/VideoCard";
import FavoriteButton from "@/components/base/FavoriteButton";
import NotFound from "@/pages/NotFound";
import {
  categoryNameMap,
  formatViews,
  getRelatedVideos,
  getVideoById,
} from "@/mocks/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { videoId } = useParams();
  const video = videoId ? getVideoById(videoId) : undefined;

  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(8);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + 0.6));
    }, 200);
    return () => clearInterval(timer);
  }, [playing]);

  useEffect(() => {
    setPlaying(false);
    setProgress(8);
  }, [videoId]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => (video ? getRelatedVideos(video, 6) : []), [video]);

  usePageMeta(
    video ? `${video.title} - 亲嘴视频` : "亲嘴视频 - 精选亲吻视频合集在线播放",
    video
      ? `${video.title}，属于${categoryNameMap[video.categoryKey]}分类，时长 ${video.duration}，${video.description}`
      : "亲嘴视频为您提供丰富的亲吻视频资源。"
  );

  if (!video) return <NotFound />;

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，快去分享吧（模拟分享）");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <SiteLayout>
      {toast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-in">
          {toast}
        </div>
      )}

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link
            to={`/category/${video.categoryKey}`}
            className="hover:text-primary-600 transition-colors cursor-pointer"
          >
            {categoryNameMap[video.categoryKey]}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800 line-clamp-1">{video.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8">
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950">
              <img
                src={video.cover}
                alt={`${video.title} - 亲吻视频播放`}
                title={`${video.title} - 亲嘴视频`}
                className={`w-full h-full object-cover object-top transition-all duration-500 ${
                  playing ? "opacity-90 scale-105" : "opacity-70"
                }`}
              />
              <div className="absolute inset-0 bg-foreground-950/25" />

              <button
                onClick={() => setPlaying((v) => !v)}
                aria-label={playing ? "暂停" : "播放"}
                className="absolute inset-0 flex items-center justify-center cursor-pointer group"
              >
                <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 group-hover:bg-primary-500 transition-colors">
                  <i className={`text-3xl md:text-4xl ${playing ? "ri-pause-fill" : "ri-play-fill"}`} />
                </span>
              </button>

              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 bg-gradient-to-t from-foreground-950/90 to-transparent">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setPlaying((v) => !v)}
                    aria-label={playing ? "暂停" : "播放"}
                    className="text-background-50 cursor-pointer"
                  >
                    <i className={`text-xl ${playing ? "ri-pause-fill" : "ri-play-fill"}`} />
                  </button>
                  <div className="flex-1 h-1.5 rounded-full bg-background-50/30 overflow-hidden">
                    <div
                      className="h-full bg-primary-500 transition-[width] duration-200 ease-linear"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-xs text-background-200 font-label">
                    {video.duration}
                  </span>
                </div>
              </div>

              <span className="absolute top-3 left-3 px-2.5 py-1 rounded bg-primary-500 text-background-50 text-xs font-label">
                演示播放器
              </span>
            </div>

            <p className="mt-3 text-xs text-foreground-500">
              说明：本站为内容演示站点，播放器仅提供播放 / 暂停交互演示，不接入真实视频流。
            </p>

            <div className="mt-6">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-snug mb-3">
                {video.title}
              </h1>
              <div className="flex items-center gap-x-5 gap-y-2 flex-wrap text-sm text-foreground-500 font-label mb-5">
                <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900">
                  {categoryNameMap[video.categoryKey]}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {video.uploadedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  时长 {video.duration}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-global-line" />
                  {video.region}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-play-circle-line" />
                  {formatViews(video.views)} 次播放
                </span>
              </div>

              <div className="flex items-center gap-3 flex-wrap mb-6">
                <FavoriteButton videoId={video.id} withLabel />
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-4 h-11 rounded-md bg-secondary-100 text-secondary-900 text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" />
                  分享链接
                </button>
                <Link
                  to={`/category/${video.categoryKey}`}
                  className="inline-flex items-center gap-2 px-4 h-11 rounded-md border border-background-300/70 text-foreground-700 text-sm hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  返回分类
                </Link>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
                <h2 className="font-heading text-lg text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-file-text-line text-primary-500" />
                  视频简介
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  {video.description}
                </p>
                <p className="text-sm text-foreground-600 mb-4">
                  <span className="text-foreground-500">影视出处：</span>
                  {video.source}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-label"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5 lg:sticky lg:top-24">
              <h2 className="font-heading text-xl text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-movie-2-line text-primary-500" />
                相关推荐
              </h2>
              <div className="space-y-3">
                {related.map((item, index) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex gap-3 cursor-pointer"
                  >
                    <div className="relative w-28 md:w-32 shrink-0 aspect-video rounded-md overflow-hidden bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title} - 相关亲吻视频`}
                        title={`${item.title} - 亲嘴视频`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-foreground-950/75 text-background-50 text-[10px] font-label">
                        {item.duration}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground-950 font-medium line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </p>
                      <p className="text-xs text-foreground-500 mt-1">
                        {formatViews(item.views)} 次播放
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">
            你可能还喜欢
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {related.slice(0, 3).map((item, index) => (
              <VideoCard key={item.id} video={item} index={index} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}